import { DatePicker, TimePicker, Checkbox, Radio } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import styled from "styled-components";

import { Button } from "@components/Button";
import ErrorValidate from "@components/ErrorValidate";
import { StyledInput } from "@components/Input";
import { Text } from "@components/Text";
import { COLOR_DARK, COLOR_GREEN, COLOR_WHITE } from "@static/COLORS";
import { fetchAPI } from "@utils/https/fetchAPI";
import { VALIDATE_RULES } from "@validate/index";

const { RangePicker } = DatePicker;
const { Group: RadioGroup } = Radio;

const FormContainer = styled.form`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 2px;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: 8px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 40px;
`;

const SectionContainerDayOfWeeks = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  justify-content: center;
  margin-bottom: 16px;
`;

const SectionContainerTime = styled.div`
  display: flex;
  gap: 20px;
`;

const GroupInput = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const OPTION_STATUS = [
  { label: "Draft", value: "DRAFT" },
  { label: "On Air", value: "ON-AIR" },
];

const DAY_OF_WEEK_LIST = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const FormEdit = ({
  customerId,
  locationId,
  setDataList = () => undefined,
  setIsModalVisible,
  // dataList,
  selectedData,
  setSelectedData,
  // locationOptions = [],
  // customerOptions = [],
  // userProfile,
}) => {
  const initialIsNoEnd = dayjs(_.get(selectedData, ["end_date"])).isSame(
    "3000-01-01",
    "day"
  );
  const initialIsAllTime =
    _.get(selectedData, ["start_time"]) === "00:00:00" &&
    _.get(selectedData, ["end_time"]) === "23:59:59";

  const today = dayjs().format("YYYY-MM-DD");
  const [isNoEnd, setIsNoEnd] = useState(initialIsNoEnd);
  const [isAllTime, setIsAllTime] = useState(initialIsAllTime);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      name: _.get(selectedData, ["name"]),
      dateRange: [
        dayjs(_.get(selectedData, ["start_date"])),
        dayjs(_.get(selectedData, ["end_date"])),
      ],
      startTime: dayjs(`${today}T${_.get(selectedData, ["start_time"])}`),
      endTime: dayjs(`${today}T${_.get(selectedData, ["end_time"])}`),
      width: _.get(selectedData, ["width"]),
      height: _.get(selectedData, ["height"]),
      status: _.get(selectedData, ["status"]),
      daysOfWeek: _.chain(selectedData)
        .get("days_of_week")
        .mapValues((value) => value === "true")
        .value(),
    },
  });

  const onHandleEdit = async (data) => {
    const name = _.get(data, ["name"]);
    const days_of_week = _.get(data, ["daysOfWeek"]);
    const start_time = dayjs(_.get(data, ["startTime"])).format("HH:mm:ss");
    const end_time = dayjs(_.get(data, ["endTime"])).format("HH:mm:ss");
    const status = _.get(data, ["status"]);
    const width = _.get(data, ["width"]);
    const height = _.get(data, ["height"]);
    const date_range_start_raw = _.get(data, ["dateRange", 0]);
    const date_range_end_raw = _.get(data, ["dateRange", 1]);

    const start_date = dayjs(date_range_start_raw).format("YYYY-MM-DD");
    const end_date = dayjs(date_range_end_raw).format("YYYY-MM-DD");

    const reqData = {
      id: _.get(selectedData, ["id"]),
      name,
      days_of_week,
      start_time,
      end_time,
      status,
      width,
      height,
      start_date,
      end_date,
    };

    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/advertising-campaigns-update",
      isAuth: true,
      data: {
        id: _.get(selectedData, ["id"]),
        name,
        days_of_week,
        start_time,
        end_time,
        status,
        width,
        height,
        start_date,
        end_date,
        category_customer_id: customerId,
        location_id: locationId,
      },
    });
    if (_.get(resp, ["status"])) {
      const results = _.chain(resp).get(["result"]).value();
      const updateData = _.chain(results).value();
      toast.success("Update Campaign Success.");
      setIsModalVisible((prev) => ({ ...prev, isOpen: false }));
      setSelectedData(null);
      setDataList((prev) => {
        const updateDataList = _.map(prev, (item) => {
          return _.get(item, ["id"]) ===
            _.chain(results).get(["id"]).toInteger().value()
            ? { ...item, ...updateData }
            : item;
        });
        return updateDataList;
      });
    }
  };

  const handleNoEndChange = (checked) => {
    setIsNoEnd(checked);
    if (checked) {
      setValue("dateRange", [dayjs("1990-01-01"), dayjs("3000-01-01")]);
    } else {
      setValue("dateRange", [
        dayjs().subtract(1, "day"),
        dayjs().add(1, "month"),
      ]);
    }
  };

  const onHandleAllTimeChange = (checked) => {
    setIsAllTime(checked);
    if (checked) {
      setValue("startTime", dayjs("00:00:00", "HH:mm:ss"));
      setValue("endTime", dayjs("23:59:59", "HH:mm:ss"));
    } else {
      setValue("startTime", dayjs("09:00:00", "HH:mm:ss"));
      setValue("endTime", dayjs("22:00:00", "HH:mm:ss"));
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onHandleEdit)} autoComplete="off">
      <Line />
      <InnerContainer style={{ overflow: "scroll" }}>
        <div>
          <Controller
            name="name"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Name
                </Text>
                <StyledInput
                  label="name"
                  {...field}
                  size="large"
                  placeholder="name"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <GroupInput>
            <Controller
              name="dateRange"
              control={control}
              rules={_.get(VALIDATE_RULES, ["required"])}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <Text
                    color={COLOR_DARK}
                    fontSize="14px"
                    style={{ marginBottom: 4, marginLeft: 4 }}
                  >
                    Date Range
                  </Text>
                  <RangePicker
                    {...field}
                    disabled={isNoEnd}
                    className={isNoEnd && "picker-no-limit"}
                    style={{ width: "100%", height: 40 }}
                  />
                  <ErrorValidate justify="start" error={error} />
                </div>
              )}
            />
            <CheckboxContainer>
              <Text color={COLOR_DARK} fontSize="14px">
                Forever
              </Text>
              <Checkbox
                style={{ marginTop: 10 }}
                checked={isNoEnd}
                onChange={(e) => handleNoEndChange(e.target.checked)}
              ></Checkbox>
            </CheckboxContainer>
          </GroupInput>
          <GroupInput>
            <SectionContainerTime>
              <Controller
                name="startTime"
                control={control}
                rules={_.get(VALIDATE_RULES, ["required"])}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ width: "100%" }}>
                    <Text
                      color={COLOR_DARK}
                      fontSize="14px"
                      style={{ marginBottom: 4, marginLeft: 4 }}
                    >
                      Start Time
                    </Text>
                    <TimePicker
                      className={isAllTime && "picker-no-limit"}
                      disabled={isAllTime}
                      style={{ width: "100%", height: 40 }}
                      {...field}
                      showNow={false}
                      needConfirm={false}
                      onChange={(value) => field.onChange(value)}
                    />
                    <ErrorValidate justify="start" error={error} />
                  </div>
                )}
              />

              <Controller
                name="endTime"
                control={control}
                rules={_.get(VALIDATE_RULES, ["required"])}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ width: "100%" }}>
                    <Text
                      color={COLOR_DARK}
                      fontSize="14px"
                      style={{ marginBottom: 4, marginLeft: 4 }}
                    >
                      End Time
                    </Text>
                    <TimePicker
                      className={isAllTime && "picker-no-limit"}
                      disabled={isAllTime}
                      showNow={false}
                      needConfirm={false}
                      style={{ width: "100%", height: 40 }}
                      {...field}
                      onChange={(value) => field.onChange(value)}
                    />
                    <ErrorValidate justify="start" error={error} />
                  </div>
                )}
              />
            </SectionContainerTime>

            <CheckboxContainer>
              <Text color={COLOR_DARK} fontSize="14px">
                All Time
              </Text>
              <Checkbox
                checked={isAllTime}
                style={{ marginTop: 10 }}
                onChange={(e) => onHandleAllTimeChange(e.target.checked)}
              />
            </CheckboxContainer>
          </GroupInput>

          <div>
            <Text
              color={COLOR_DARK}
              fontSize="14px"
              style={{ marginBottom: 4, marginLeft: 4 }}
            >
              Day of weeks
            </Text>

            <SectionContainerDayOfWeeks>
              {_.map(DAY_OF_WEEK_LIST, (item) => (
                <Controller
                  key={item}
                  name={`daysOfWeek.${_.toLower(item)}`}
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} checked={field.value}>
                      {item}
                    </Checkbox>
                  )}
                />
              ))}
            </SectionContainerDayOfWeeks>
          </div>

          <Controller
            name="width"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Width
                </Text>
                <StyledInput
                  label="width"
                  {...field}
                  size="large"
                  placeholder="width"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="height"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Height
                </Text>
                <StyledInput
                  label="height"
                  {...field}
                  size="large"
                  placeholder="height"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="status"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Status
                </Text>
                <RadioGroup
                  {...field}
                  options={OPTION_STATUS}
                  optionType="button"
                  style={{
                    borderColor: error ? "red" : undefined,
                  }}
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />
        </div>
        <ButtonContainer>
          <Button
            type="submit"
            color={COLOR_GREEN}
            fontColor={COLOR_WHITE}
            fontSize="16px"
            padding="5px 20px"
          >
            Save
          </Button>
        </ButtonContainer>
      </InnerContainer>
    </FormContainer>
  );
};
